import React, { useEffect, useState } from "react";
import {
  Table,
  Avatar,
  Space,
  Typography,
  Button,
  message,
  Select,
  Input,
} from "antd";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { ReactComponent as VerifiedIcon } from "../../../Assets/images/icon/verifiedIcon.svg";
import DeleteUserModal from "./DeleteUserModal";
import { fetchUserList, deleteUser } from "../useAPIDetails/useFetchUserList";
import { debounce } from "lodash";

const { Title } = Typography;
const { Option } = Select;

const AllUserDetails = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState(null);
  const [filterStatus, setFilterStatus] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");

  const handleExport = (data) => {
    if (!data || data.length === 0) {
      console.error("No data available to export.");
      return;
    }
    const formattedData = data.map((user) => ({
      "User ID": user.key || "-",
      User: user.user || "-",
      "User Name": user.username || "-",
      Verified: user.verified ? "Yes" : "No",
      Email: user.email || "-",
      "Phone Number": user.phoneNumber || "-",
      Privacy: user.privacy || "-",
      Status: user.status || "-",
      Posts: user.posts ?? "-",
      Likes: user.likes ?? "-",
      Recreations: user.recreations ?? "-",
      Followers: user.followers ?? "-",
    }));
    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "User_Data.xlsx");
  };

  const debouncedSearch = debounce(async (query, page, size, status) => {
    setLoading(true);
    try {
      const params = {
        page: page - 1,
        size: size,
        ...(status !== "all" && { status }),
        ...(query && { searchQuery: query }),
      };

      const result = await fetchUserList(params);

      setData(
        result.data.userProfiles?.map((user) => ({
          key: user.id,
          user: `${user?.firstName || "-"} ${user?.lastName || "-"}`,
          username: user?.userName || "-",
          email: user?.email || "-",
          phoneNumber: user?.phoneNumber || "-",
          privacy: user?.privateAccount ? "Private" : "Public",
          status: user?.userStatus || "-",
          posts:
            user?.additionalInfo?.postsCount +
              user?.additionalInfo?.recipesCount ?? "-",
          likes: user?.additionalInfo?.likesCount ?? "-",
          recreations: user?.additionalInfo?.totalRecreations ?? "-",
          followers: user?.additionalInfo?.followersCount ?? "-",
          verified: user?.verified || false,
        }))
      );

      setPagination({
        current: page,
        pageSize: size,
        total: result.data.pagination.totalElements,
      });
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load user list."
      );
    } finally {
      setLoading(false);
    }
  }, 1000);

  // Initial and filtered data load
  useEffect(() => {
    debouncedSearch(
      searchQuery,
      pagination.current,
      pagination.pageSize,
      filterStatus
    );
  }, [filterStatus, searchQuery]); // dont add pagination here it created infinity loop

  // Handle table pagination
  const handleTableChange = (pagination) => {
    debouncedSearch(
      searchQuery,
      pagination.current,
      pagination.pageSize,
      filterStatus
    );
  };

  // Handle filter status change
  const handleFilterChange = (value) => {
    setFilterStatus(value);
  };

  // Handle search input
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (text, record) => (
        <Space size="middle">
          <Avatar style={{ backgroundColor: "#f56a00" }}>{text[0]}</Avatar>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <span>{text}</span>
            {record.verified && (
              <VerifiedIcon style={{ width: "30px", height: "30px" }} />
            )}
          </div>
        </Space>
      ),
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      align: "center",
    },
    {
      title: "Privacy",
      dataIndex: "privacy",
      key: "privacy",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (text) =>
        text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "",
    },
    {
      title: "Posts",
      dataIndex: "posts",
      key: "posts",
      align: "center",
    },
    {
      title: "Likes",
      dataIndex: "likes",
      key: "likes",
      align: "center",
    },
    {
      title: "Recreations",
      dataIndex: "recreations",
      key: "recreations",
      align: "center",
    },
    {
      title: "Followers",
      dataIndex: "followers",
      key: "followers",
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          danger
          type="link"
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(record);
          }}
        />
      ),
    },
  ];

  // Handle delete
  const handleDelete = (user) => {
    setSelectedUser(user.key);
    setSelectedUserName(user.user);
    setIsModalVisible(true);
  };

  const confirmDelete = async () => {
    setIsModalVisible(false);
    try {
      await deleteUser(selectedUser);
      message.success("User deleted successfully.");
      debouncedSearch(
        searchQuery,
        pagination.current,
        pagination.pageSize,
        filterStatus
      );
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          "Failed to delete user. Please try again."
      );
    }
  };

  return (
    <div className="flex-grow-content" style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Users Management
        </Title>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            className="primary-button"
            variant="solid"
            onClick={() => handleExport(data)}
            style={{ marginRight: "10px" }}
          >
            Export
          </Button>
          <Input
            placeholder="Search users"
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={handleSearch}
            style={{ width: "200px" }}
          />
          <Select
            defaultValue="active"
            style={{ width: 120, height: "35px" }}
            onChange={handleFilterChange}
          >
            <Option value="active">Active</Option>
            <Option value="deleted">Deleted</Option>
            <Option value="deactivated">Deactivated</Option>
            <Option value="all">All</Option>
          </Select>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={data ? data : []}
        loading={loading}
        rowKey={(record) => record.key}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => {
            const url = `/userdetails/${record.key}`;
            window.open(url, "_blank");
          },
        })}
        style={{
          border: "1px solid #E1E1E1",
          borderRadius: "5px",
          cursor: "pointer",
        }}
      />
      <DeleteUserModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        onConfirm={confirmDelete}
        userId={selectedUser}
        userName={selectedUserName}
      />
    </div>
  );
};

export default AllUserDetails;
